import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import apiFetch from "../../utlis/apiFetch"
import { forceLogout } from "../../utlis/forceLogout"

const handleAuthError = (error, rejectWithValue) => {
  if (error.message === "Your session has expired") forceLogout()
  return rejectWithValue(error.message)
}

// Fetch employee profile data with caching mechanism
export const fetchEmployeeProfile = createAsyncThunk(
  "employeeProfile/fetchEmployeeProfile",
  async (userId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token")
      if (!token) throw new Error("No authentication token found")

      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/application/agentProfile?user=${userId}`

      const response = await apiFetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })

      const data = response._data ?? await response.json()

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch employee profile")
      }

      return {
        data: {
          ...data.body,
          userId, // Store the userId with the profile data
        },
        userId,
      }
    } catch (err) {
      return handleAuthError(err, rejectWithValue)
    }
  },
)

// Update agent details
export const updateAgent = createAsyncThunk(
  "employeeProfile/updateAgent",
  async ({ agentId, formData }, { rejectWithValue, getState }) => {
    try {
      const token = localStorage.getItem("token")
      if (!token) throw new Error("No authentication token found")

      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/user/editAgent?agentId=${agentId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formData),
        }
      )

      const data = response._data ?? await response.json()

      if (!response.ok) {
        throw new Error(data.message || "Failed to update agent")
      }

      // Return the updated agent data from the response
      return { agentId, updatedAgent: data.body }
    } catch (err) {
      return handleAuthError(err, rejectWithValue)
    }
  }
)

// Get application count for an agent
export const getAgentApplicationCount = createAsyncThunk(
  "employeeProfile/getAgentApplicationCount",
  async (agentId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token")
      if (!token) throw new Error("No authentication token found")

      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/application/getApplicationCount/${agentId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      )

      const data = response._data ?? await response.json()

      if (!response.ok) {
        throw new Error(data.message || "Failed to get application count")
      }

      return { agentId, count: data.body.count }
    } catch (err) {
      return handleAuthError(err, rejectWithValue)
    }
  }
)

// Deactivate agent
export const deactivateAgent = createAsyncThunk(
  "employeeProfile/deactivateAgent",
  async ({ agentId, newAgentId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token")
      if (!token) throw new Error("No authentication token found")
      let url = `${process.env.REACT_APP_API_BASE_URL}/api/user/deactivateAgent?agentId=${agentId}`;
      if (newAgentId) {
        url += `&newAgentId=${newAgentId}`;
      }
      const response = await apiFetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
      )

      const data = response._data ?? await response.json()

      if (!response.ok) {
        throw new Error(data.message || "Failed to deactivate agent")
      }

      return { agentId, newAgentId }
    } catch (err) {
      return handleAuthError(err, rejectWithValue)
    }
  }
)

// Redux Slice
const employeeProfileSlice = createSlice({
  name: "employeeProfile",
  initialState: {
    employeeProfilesById: {}, // Object to store employee profiles by userId
    currentEmployeeId: null, // Track the currently viewed employee
    currentEmployeeProfile: null, // Current employee profile data
    applicationCount: null, // Application count for deactivation
    isLoading: false,
    isUpdating: false,
    isDeactivating: false,
    error: null,
  },
  reducers: {
    setCurrentEmployeeId: (state, action) => {
      const userId = action.payload
      state.currentEmployeeId = userId

      // If we have this employee's profile in cache, set it as current
      if (state.employeeProfilesById[userId]) {
        state.currentEmployeeProfile = state.employeeProfilesById[userId]
      } else {
        state.currentEmployeeProfile = null
      }
    },
    clearEmployeeProfiles: (state) => {
      state.employeeProfilesById = {}
      state.currentEmployeeProfile = null
      state.currentEmployeeId = null
    },
    clearCurrentEmployeeProfile: (state) => {
      state.currentEmployeeProfile = null
      state.currentEmployeeId = null
      state.isLoading = false
      state.error = null
    },
    clearApplicationCount: (state) => {
      state.applicationCount = null
    },
    // New reducer to update agent in the agents list (for grid view)
    updateAgentInList: (state, action) => {
      const updatedAgent = action.payload;
      if (state.employeeProfilesById[updatedAgent._id]) {
        state.employeeProfilesById[updatedAgent._id] = {
          ...state.employeeProfilesById[updatedAgent._id],
          ...updatedAgent
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployeeProfile.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchEmployeeProfile.fulfilled, (state, action) => {
        state.isLoading = false

        // Store latest profile so revisits always reflect fresh API data
        state.employeeProfilesById[action.payload.userId] = action.payload.data

        // Set as current employee profile
        state.currentEmployeeProfile = action.payload.data
        state.currentEmployeeId = action.payload.userId
      })
      .addCase(fetchEmployeeProfile.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
      .addCase(updateAgent.pending, (state) => {
        state.isUpdating = true
        state.error = null
      })
      .addCase(updateAgent.fulfilled, (state, action) => {
        state.isUpdating = false
        const { agentId, updatedAgent } = action.payload

        // Update the agent in the cache
        if (state.employeeProfilesById[agentId]) {
          state.employeeProfilesById[agentId] = {
            ...state.employeeProfilesById[agentId],
            ...updatedAgent
          }
        }

        // Update current profile if it's the one being edited
        if (state.currentEmployeeId === agentId) {
          state.currentEmployeeProfile = {
            ...state.currentEmployeeProfile,
            ...updatedAgent
          }
        }
      })
      .addCase(updateAgent.rejected, (state, action) => {
        state.isUpdating = false
        state.error = action.payload
      })
      .addCase(getAgentApplicationCount.fulfilled, (state, action) => {
        state.applicationCount = action.payload.count
      })
      .addCase(deactivateAgent.pending, (state) => {
        state.isDeactivating = true
        state.error = null
      })
      .addCase(deactivateAgent.fulfilled, (state, action) => {
        state.isDeactivating = false
        // Remove the deactivated agent from the cache
        delete state.employeeProfilesById[action.payload.agentId]
        // Clear current profile if it's the deactivated one
        if (state.currentEmployeeId === action.payload.agentId) {
          state.currentEmployeeProfile = null
          state.currentEmployeeId = null
        }
        state.applicationCount = null
      })
      .addCase(deactivateAgent.rejected, (state, action) => {
        state.isDeactivating = false
        state.error = action.payload
      })
  },
})

export const { 
  setCurrentEmployeeId, 
  clearEmployeeProfiles, 
  clearCurrentEmployeeProfile,
  clearApplicationCount,
  updateAgentInList 
} = employeeProfileSlice.actions

// Selectors for easy access to state
export const selectEmployeeProfileById = (state, userId) => {
  return state.employeeProfile.employeeProfilesById[userId] || null
}

export const selectCurrentEmployeeProfile = (state) => state.employeeProfile.currentEmployeeProfile

export const selectCurrentEmployeeId = (state) => state.employeeProfile.currentEmployeeId

export const selectIsLoading = (state) => state.employeeProfile.isLoading

export const selectIsUpdating = (state) => state.employeeProfile.isUpdating

export const selectIsDeactivating = (state) => state.employeeProfile.isDeactivating

export const selectApplicationCount = (state) => state.employeeProfile.applicationCount

export const selectError = (state) => state.employeeProfile.error

export default employeeProfileSlice.reducer